import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Context/AuthContext";
import { useState } from "react";
import useSWR from "swr";
import {
  modelTestUrls,
  orderUrls,
  StaticPageApiUrls,
  UserApiUrls,
} from "../../../Api/Auth/ApiUrls";
import {
  customFetcher,
  fetcher,
  sendPostRequest,
  sendPostRequestAuth,
} from "../../../Api/swrConfig";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import { Loader } from "../../../Components/Common/Loader";
import { motion } from "framer-motion";
import HeroSection from "../../../Components/Common/HeroSection";
import Button from "../../../Components/Common/Button";
import { publicUrls, studentUrls } from "../../../Api/Auth/AppUrls";
import Modal from "../../../Components/Common/Modal";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import useToastError from "../../../hooks/useErrorToast";
import Input from "../../../Components/Common/Input";
import FreeSimulatorModal from "../../Student/Simulator/Components/FreeSimulatorModal";
const stripePromise = loadStripe(process.env.REACT_APP_Publishable_Key);
const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a" },
  },
};

const PaymentForm = ({ clientSecret, onClose, mutate }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  const { data } = useSWR(UserApiUrls.savedCards, fetcher);
  const savedCards = data?.cards || [];

  const { trigger } = useSWRMutation(orderUrls.payment, sendPostRequestAuth, {
    onSuccess: (data) => {
      mutate();
      toast.info(
        `You can access your simulator from your student dashboard too`,
      );
    },
    onError: (error) => {
      toast.error(error.message || "Payment failed. Please try again.");
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    // Get the card number element explicitly
    try {
      let paymentMethodId;

      if (selectedCard) {
        paymentMethodId = selectedCard; // Use selected saved card
      } else {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
          toast.error("Card details not found. Please try again.");
          setIsProcessing(false);
          return;
        }

        // Create a payment method using the card details
        const { paymentMethod, error: pmError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });

        if (pmError) {
          toast.error(pmError.message || "Failed to create payment method.");
          setIsProcessing(false);
          return;
        }
        paymentMethodId = paymentMethod.id;
      }
      // Confirm payment
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId,
          setup_future_usage: "off_session",
        });

      if (confirmError) {
        toast.error(
          confirmError.message || "Payment failed. Please try again.",
        );
      } else if (paymentIntent?.status === "succeeded") {
        toast.success("Payment successful!");

        // Send request to backend to update order status
        await trigger({
          paymentIntentId: paymentIntent.id,
          itemName: "Simulator(Mechanical)",
          saveCard: saveCard,
        });

        onClose();
      }
    } catch (err) {
      const dupCardError =
        err?.response?.data?.message ===
        "The payment method you provided is already attached to your saved cards list. Please use your saved card instead.";
      if (dupCardError) {
        return toast.error(err?.response?.data?.message);
      }
      console.error("Payment error:", err);
      toast.error("An unexpected error occurred.");
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg p-3">
      {savedCards.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Saved Cards
          </label>
          <select
            className="p-2 border rounded-md w-full"
            value={selectedCard || ""}
            onChange={(e) => setSelectedCard(e.target.value || null)}
          >
            <option value="">Use a new card</option>
            {savedCards.map((card) => (
              <option key={card.id} value={card.id}>
                {`**** **** **** ${
                  card.card.last4
                } (${card.card.brand.toUpperCase()})`}
              </option>
            ))}
          </select>
        </div>
      )}

      {!selectedCard && (
        <>
          <label className="block text-sm font-medium text-gray-700">
            Card Number
          </label>
          <CardNumberElement
            className="p-2 border rounded-md"
            options={cardElementOptions}
          />

          <div className="flex space-x-4">
            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <CardExpiryElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>

            <div className="w-1/2">
              <label className="block text-sm font-medium text-gray-700">
                CVC
              </label>
              <CardCvcElement
                className="p-2 border rounded-md w-full"
                options={cardElementOptions}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="saveCard"
              checked={saveCard}
              onChange={() => setSaveCard(!saveCard)}
              className="h-4 w-4"
            />
            <label htmlFor="saveCard" className="text-sm text-gray-700">
              Save this card for future payments
            </label>
          </div>
        </>
      )}

      <Button type="primary" className="mt-4 w-full" disabled={isProcessing}>
        {isProcessing ? "Processing..." : "Pay Now"}
      </Button>
    </form>
  );
};

const SimulatorMechanical = () => {
  const location = useLocation();
  const { showErrorToast } = useToastError();
  const { token, userData } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFreeTestModalOpen, setIsFreeTestModalOpen] = useState(false);
  const [selectedFreeTest, setSelectedFreeTest] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentConfirmScreen, setPaymentConfirmScreen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [freeTestEmail, setFreeTestEmail] = useState("");
  const [isFreeModalOpen, setIsFreeModalOpen] = useState(false);
  const slug =
    location.pathname.replace("mechanical/", "mechanical-") +
    "?subscription=true";
  const navigate = useNavigate();

  const { data, isLoading, mutate } = useSWR(
    slug ? StaticPageApiUrls.info(slug) : null,
    (url) => customFetcher(url, token),
  );
  const handleOpenFreeTest = (freeTest) => {
    setSelectedFreeTest(freeTest);
    // Check if email exists in sessionStorage
    const savedEmail = sessionStorage.getItem("freeTestEmail");
    if (savedEmail) {
      setFreeTestEmail(savedEmail);
      setIsFreeModalOpen(true);
    } else {
      // Show the email modal if no saved email
      setIsFreeTestModalOpen(true);
    }
  };
  const handleCloseFreeModal = () => {
    setIsFreeModalOpen(false);
    setSelectedFreeTest(null);
  };

  const content = data?.data?.content ? JSON.parse(data?.data?.content) : null;

  const { trigger: createFreeTestTaker, isMutating: isCreatingFreeTest } =
    useSWRMutation(modelTestUrls.createFreeModelTestTaker, sendPostRequest, {
      onSuccess: (data) => {
        sessionStorage.setItem("freeTestEmail", freeTestEmail);
        toast.success("Successfully registered for free test!");
        setIsFreeTestModalOpen(false);
        setFreeTestEmail("");
        setIsFreeModalOpen(true);
      },
      onError: (error) => {
        showErrorToast(error || "Failed to register. Please try again.");
      },
    });

  const handleFreeTestSubmit = (e) => {
    e.preventDefault();
    if (!freeTestEmail) {
      return toast.error("Email is required");
    }

    createFreeTestTaker({
      modelTest: selectedFreeTest._id,
      email: freeTestEmail,
    });
  };

  const { trigger: promoTrigger } = useSWRMutation(
    orderUrls.promo,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        const calculatedPrice =
          content?.subscription_price -
          (content?.subscription_price * data?.discount) / 100;
        setDiscountedPrice(parseFloat(calculatedPrice.toFixed(2)));
        setAppliedPromo(promoCode);
        toast.success(`Promo Code applied successfully`);
      },
      onError: (error) => {
        console.log(error);
        showErrorToast(error || "Invalid promo code. Please try again.");
      },
    },
  );

  const onBuyClick = (item) => {
    setPaymentConfirmScreen(true);
    setIsModalOpen(true);
  };

  const onBuy = async (item) => {
    const payload = {
      userId: userData?._id,
      promo: discountedPrice ? appliedPromo : undefined,
      // modelTestId: item._id,
      slug: slug,
      subscriptionId: item._id,
      customer: {
        id: userData?._id,
        name: userData?.name,
        email: userData?.email,
      },
    };
    await purchaseItem(payload);
  };

  const handleApplyPromo = async (e) => {
    e.preventDefault();
    if (!promoCode) {
      return toast.error("Enter Promo Code");
    }
    await promoTrigger({
      promoCode: promoCode,
      slug: slug,
      price: content?.subscription_price,
    });
  };

  const { trigger: purchaseItem, isMutating } = useSWRMutation(
    orderUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Processing payment...");
        setClientSecret(data.clientSecret);
        setIsModalOpen(true);
      },
      onError: (error) => {
        toast.error(error.message || "Payment failed. Please try again.");
      },
    },
  );
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 1, y: -40 }} // Start above the view
      whileInView={{ opacity: 1, y: 0 }} // End at normal position
      transition={{ duration: 1, ease: "easeOut" }} // Longer transition for more emphasis
    >
      <HeroSection
        title={
          data?.data?.displayTitle ||
          data?.data?.title ||
          "Mechanical Simulator"
        }
        backgroundImage="https://storage.googleapis.com/road-to-fe/uploads/1741417535659-07d447cd-82a2-4b1b-8c08-9fffeaaefa18-colleagues-doing-team-work-project_23-2149361607.avif"
        subtitle={
          data?.data?.headerDescription ||
          "One Platform Many Options To Explore"
        }
      />
      <div className="gap-3 container mx-auto sm:mt-12 mt-4 flex justify-center md:justify-between items-start flex-wrap md:flex-nowrap px-2">
        <div
          className="max-w-5xl w-full"
          dangerouslySetInnerHTML={{ __html: data?.data?.bodyDescription }}
        />
        <div>
          <img
            src={data?.data?.image}
            alt="Tutoring"
            className="w-full h-72 object-cover rounded-md"
          />
        </div>
      </div>
      <h2 className="text-3xl font-semibold text-primary text-center mt-4">
        {`Try our ${
          data?.data?.displayTitle || data?.data?.title || "Simulator"
        }!`}
      </h2>
      <div className="flex justify-center items-center gap-6 flex-wrap px-4">
        {data?.data?.modelTest?.map((test) => {
          // If it's a free test (price === 0)
          if (test.price === 0) {
            return (
              <div
                key={test._id}
                className="p-6 my-4 border rounded-xl shadow-xl bg-primary text-center max-w-md text-white"
              >
                <div className="mb-2">
                  <span className="inline-block bg-white text-primary text-xs font-bold px-3 py-1 rounded-full">
                    FREE
                  </span>
                </div>
                <h2 className="text-lg font-bold mb-2">{test.title}</h2>
                {test.description ? (
                  <p className="text-sm text-white/90 mb-3">
                    {test.description}
                  </p>
                ) : (
                  <div className="mb-10"> </div>
                )}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between items-center px-4 py-2 bg-white/10 rounded-lg">
                    <span className="font-medium text-sm">Duration:</span>
                    <span className="mr-2"></span>
                    <span className="font-semibold">
                      {test.duration} hour(s)
                    </span>
                  </div>
                </div>
                <Button
                  type="secondary"
                  className="mt-2 w-full"
                  onClick={() => {
                    handleOpenFreeTest(test);
                  }}
                >
                  Start Now
                </Button>
              </div>
            );
          }
          return null; // Don't render paid tests here
        })}
        {data?.status?.purchased ? (
          (() => {
            const purchaseInfo = data?.status;
            const purchasedAt = purchaseInfo?.purchasedAt
              ? new Date(purchaseInfo.purchasedAt)
              : null;
            const duration = purchaseInfo?.subscription_duration
              ? parseInt(purchaseInfo.subscription_duration, 10)
              : 0;

            if (!purchasedAt || isNaN(duration) || duration <= 0) {
              return (
                <div className="text-red-600 font-semibold">
                  ⚠️ Subscription data is missing or invalid.
                </div>
              );
            }

            const expirationDate = new Date(purchasedAt);
            expirationDate.setDate(expirationDate.getDate() + duration);

            return (
              <p className="font-semibold text-center mt-4">
                You have already purchased simulator!
                <br />
                <Link
                  to={studentUrls.studentSimulatorMechanical}
                  className="text-blue-600 underline hover:text-blue-800 font-bold"
                >
                  Go to Dashboard
                </Link>{" "}
                to access your simulator.
              </p>
            );
          })()
        ) : (
          // userData ? (
          <div className="p-6 my-4 border rounded-xl shadow-xl bg-primary text-center max-w-md mx-auto text-white">
            <h2 className="text-lg font-bold mb-4">
              {data?.data?.modelTest?.filter((item) => item.price > 0)?.length}{" "}
              Model Test(s) Live Now!
            </h2>

            <div className="flex justify-between items-center px-4 py-2 rounded-lg mb-4">
              <span className="font-medium flex-1 text-left">Price:</span>
              <span className="font-semibold">
                ${content?.subscription_price}
              </span>
            </div>
            <Button
              type="secondary"
              className="mt-2 w-full"
              onClick={() =>
                !userData ? navigate("/login") : onBuy(data?.data)
              }
              disabled={isMutating}
            >
              Buy Now
            </Button>
          </div>
        )}
      </div>
      <Modal
        isOpen={isModalOpen}
        title={"Payment"}
        onCancel={() => {
          setIsModalOpen(false);
          setPaymentConfirmScreen(false);
          setPromoCode("");
          setDiscountedPrice(null);
          setAppliedPromo("");
        }}
        isLoading={isMutating}
        formWidth="sm:max-w-[50%]"
        footer={false}
      >
        {paymentConfirmScreen && (
          <div className="space-y-4">
            {/* Price Display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:w-1/2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">
                    Total Price
                  </span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {discountedPrice ? (
                    <>
                      <span className="line-through text-gray-400 mr-2">
                        ${content?.subscription_price}
                      </span>
                      <span className="text-green-600">${discountedPrice}</span>
                    </>
                  ) : (
                    <span>${content?.subscription_price}</span>
                  )}
                </div>
              </div>
              {discountedPrice && (
                <p className="text-xs text-gray-500">
                  A promo code has been applied. You saved{" "}
                  <span className="font-semibold text-green-600">
                    $
                    {parseFloat(
                      (content?.subscription_price - discountedPrice).toFixed(
                        2,
                      ),
                    )}
                  </span>
                  !
                </p>
              )}
            </div>

            {/* Promo Code Input with Apply Button */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promo Code
              </label>
              <div className="flex flex-wrap gap-2">
                <Input
                  type="text"
                  value={promoCode}
                  onChange={(_, value) => setPromoCode(value)}
                  placeholder="Enter promo code"
                  className="flex-1 p-2"
                />
                <Button
                  type="button"
                  size="small"
                  className="bg-primary text-white text-center my-2"
                  onClick={(e) => {
                    handleApplyPromo(e);
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              By purchasing, you agree to our{" "}
              <a
                href={publicUrls.TermsOfServices}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary font-medium"
              >
                terms & conditions
              </a>
              .
            </p>
            <Button
              type="button"
              className="bg-primary text-white text-center"
              onClick={() => {
                onBuy(data?.data);
              }}
            >
              Purchase
            </Button>
          </div>
        )}

        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              onClose={() => {
                setIsModalOpen(false);
                setClientSecret("");
              }}
              clientSecret={clientSecret}
              mutate={mutate}
            />
          </Elements>
        )}
      </Modal>
      {/* Free Test Modal */}
      <Modal
        isOpen={isFreeTestModalOpen}
        title={selectedFreeTest?.title || "Free Model Test"}
        onCancel={() => {
          setIsFreeTestModalOpen(false);
          setSelectedFreeTest(null);
          setFreeTestEmail("");
        }}
        formWidth="sm:max-w-[50%]"
        footer={false}
      >
        <form onSubmit={handleFreeTestSubmit} className="space-y-4 p-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-bold text-green-800 mb-2">
              🎉 Free Model Test Access
            </h3>
            <p className="text-gray-700">
              You're about to start:{" "}
              <span className="font-semibold">{selectedFreeTest?.title}</span>
            </p>
          </div>

          {selectedFreeTest && (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">
                  Duration:
                </span>
                <span className="font-semibold text-gray-900">
                  {selectedFreeTest.duration} hours
                </span>
              </div>
              {selectedFreeTest.description && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">
                    Description:
                  </span>
                  <p className="text-gray-900 mt-1">
                    {selectedFreeTest.description}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={freeTestEmail}
              onChange={(_, value) => setFreeTestEmail(value)}
              placeholder="Enter your email"
              className="w-full"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              We'll use this email to send you details.
            </p>
          </div>

          <div className="pt-4">
            <Button
              type="primary"
              className="w-full"
              disabled={isCreatingFreeTest}
            >
              {isCreatingFreeTest ? "Processing..." : "Continue to Test"}
            </Button>
          </div>
        </form>
      </Modal>
      <FreeSimulatorModal
        selected={selectedFreeTest}
        isOpen={isFreeModalOpen}
        onClose={handleCloseFreeModal}
      />
    </motion.div>
  );
};
export default SimulatorMechanical;
